import type { OnInit } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxCheckboxComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
  onyxTimeRangeValidator,
  OnyxToggleComponent,
  type OnyxTimeRange,
} from '@onyx/angular';
import { isEqual, toArray } from 'lodash';
import { map } from 'rxjs';
import { DAY_TIME_RANGE } from '../../../constants/common/day-time-range';
import { SuggestionHelper } from '../../../helpers/suggestions.helper';
import { ValidationHelper } from '../../../helpers/validation.helper';

type BusinessHoursFormGroup = ReturnType<
  (typeof BusinessHoursFormComponent)['buildForm']
>;

@Component({
  selector: 'app-business-hours-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTimepickerComponent,
    OnyxToggleComponent,
    OnyxSuggestionsComponent,
    OnyxCheckboxComponent,
    OnyxFormGroupComponent,
    OnyxMessageComponent,
    OnyxIconComponent,
    TranslatePipe,
  ],
  templateUrl: './business-hours-form.component.html',
  styleUrl: './business-hours-form.component.scss',
})
export class BusinessHoursFormComponent implements OnInit {
  private readonly injector = inject(Injector);

  public readonly type = input.required<'pointsOfInterest' | 'bases'>();
  public readonly form = input.required<BusinessHoursFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();

  protected readonly I18N = 'forms.businessHours';
  protected readonly TIME_RANGE_SUGGESTIONS =
    SuggestionHelper.getTimeRangeSuggestions();

  protected readonly OnyxFormMode = OnyxFormMode;

  protected isAlwaysOpen = signal(false);
  protected isSaturdayClosed = signal(false);
  protected isSundayClosed = signal(false);

  public ngOnInit(): void {
    const businessHours = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      { initialValue: this.form().getRawValue(), injector: this.injector },
    );

    const formValues = toArray(businessHours());
    const isAlwaysOpen =
      formValues.length > 0 &&
      formValues.every((value) => isEqual(value, DAY_TIME_RANGE));
    this.isAlwaysOpen.set(isAlwaysOpen);

    this.isSaturdayClosed.set(businessHours()?.saturday === null);
    this.isSundayClosed.set(businessHours()?.sunday === null);

    effect(
      () => {
        if (this.isSaturdayClosed() || this.isSundayClosed()) {
          this.isAlwaysOpen.set(false);
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        ValidationHelper.toggleControls(this.form(), {
          enabled: !this.isAlwaysOpen(),
          reset: true,
        });

        if (this.isAlwaysOpen()) {
          this.isSaturdayClosed.set(false);
          this.isSundayClosed.set(false);

          const controls = toArray(this.form().controls);
          for (const control of controls) {
            control.setValue(DAY_TIME_RANGE);
          }
        }

        const weekendControls = new Map([
          [this.form().controls.saturday, this.isSaturdayClosed()],
          [this.form().controls.sunday, this.isSundayClosed()],
        ]);
        for (const [control, disabled] of weekendControls) {
          if (disabled) {
            ValidationHelper.disableControls(control, { reset: true });
          }
        }
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder, isAlwaysOpen = false) {
    const form = fb.group({
      weekday: fb.control<OnyxTimeRange | null>(null, [
        Validators.required,
        onyxTimeRangeValidator,
      ]),
      saturday: fb.control<OnyxTimeRange | null>(null, [
        Validators.required,
        onyxTimeRangeValidator,
      ]),
      sunday: fb.control<OnyxTimeRange | null>(null, [
        Validators.required,
        onyxTimeRangeValidator,
      ]),
    });

    if (isAlwaysOpen) {
      for (const control of toArray(form.controls)) {
        control.setValue(DAY_TIME_RANGE);
      }
    }

    return form;
  }
}
